// @file    notification-url.service.ts
// @created 2026-06-12 by Claude
// @desc    Maps backend (Blazor) notification URLs to I4U Angular routes.
//          Extracted from the header notification dropdown so the notification-center
//          tab can reuse the exact same mapping table (single source of truth).

import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class NotificationUrlService {
  /**
   * Map a backend Blazor URL to its I4U Angular route equivalent.
   * Returns null when there is no sensible I4U destination (caller should not navigate).
   */
  mapBackendUrlToI4U(backendUrl: string | null | undefined): string | null {
    if (!backendUrl) return null;

    const urlMappings: { pattern: RegExp; transform: (match: RegExpMatchArray) => string }[] = [
      // Agent Commission routes -> agent-hub income tabs (standalone /commission retired)
      { pattern: /^\/AgentCommissionDetails\/([^\/]+)\/(.+)$/i, transform: () => `/agent-hub` },
      // Application routes
      { pattern: /^\/ApplicationAndPolicyUpdate\/(.+)$/i, transform: (m) => `/application-detail/${m[1]}` },
      { pattern: /^\/ApplicationAndPolicyUpdateBof\/(.+)$/i, transform: (m) => `/application-detail/${m[1]}` },
      { pattern: /^\/ApplicationDetail\/(.+)$/i, transform: (m) => `/application-detail/${m[1]}` },
      // Policy routes (I4U has no policy detail -> list)
      { pattern: /^\/IHPolicyDetail\/(.+)$/i, transform: () => `/application-policy-list` },
      { pattern: /^\/PolicyDetail\/(.+)$/i, transform: () => `/application-policy-list` },
      // Policy change request routes
      { pattern: /^\/PolicyChangeRequest\/Detail\/(\d+)$/i, transform: (m) => `/policy-change-request/detail/${m[1]}` },
      { pattern: /^\/PolicyChangeRequestDetail\/(\d+)$/i, transform: (m) => `/policy-change-request/detail/${m[1]}` },
      { pattern: /^\/PolicyChangeRequestBofDetails\/(.+)-PCR-(\d+)$/i, transform: (m) => `/policy-change-request/detail/${m[2]}` },
      // Policy cancel routes
      { pattern: /^\/PolicyCancel\/(.+)$/i, transform: () => `/policy-cancel` },
      { pattern: /^\/PolicyCancelationBofDetails\/(.+)-CAN(-\d+)?$/i, transform: () => `/policy-cancel` },
      { pattern: /^\/PolicyCancelRequestBofDetails\/(.+)-CAN(-\d+)?$/i, transform: () => `/policy-cancel` },
      // Claim routes
      { pattern: /^\/Claim\/Accident\/(\d+)$/i, transform: (m) => `/claim/accident-update/${m[1]}` },
      { pattern: /^\/Claim\/Dead\/(\d+)$/i, transform: (m) => `/claim/dead-update/${m[1]}` },
      { pattern: /^\/ClaimDetail\/(.+)$/i, transform: () => `/claim` },
      { pattern: /^\/ClaimAccidentUpdateBof\/(.+)$/i, transform: (m) => `/claim/accident-update/${m[1]}` },
      { pattern: /^\/ClaimDeadUpdateBof\/(.+)$/i, transform: (m) => `/claim/dead-update/${m[1]}` },
      { pattern: /^\/I4UClaimAccidentUpdate\/(.+)$/i, transform: (m) => `/claim/accident-update/${m[1]}` },
      { pattern: /^\/I4UClaimDeadUpdate\/(.+)$/i, transform: (m) => `/claim/dead-update/${m[1]}` },
      // Premium routes
      { pattern: /^\/Premium\/History\/(\d+)$/i, transform: (m) => `/premium-history-detail/${m[1]}` },
      { pattern: /^\/PremiumHistory\/(.+)$/i, transform: () => `/premium-history-list` },
      { pattern: /^\/I4UPremiumHistoryDetail\/(.+)$/i, transform: (m) => `/premium-history-detail/${m[1]}` },
      { pattern: /^\/PremiumHistoryListBofDetail\/(.+)$/i, transform: (m) => `/premium-history-list-detail/${m[1]}` },
      { pattern: /^\/IHPayPremiumHistory\/(.+)$/i, transform: (m) => `/pay-premium-history/${m[1]}` },
      { pattern: /^\/PayPremiumFromBonusBofDetails\/([^_]+)_(.+)$/i, transform: (m) => `/pay-premium-from-bonus-details/${m[1]}/${m[2]}` },
      // Commission routes -> agent-hub income tabs (standalone /commission retired)
      { pattern: /^\/Commission/i, transform: () => `/agent-hub` },
      // Product Interest routes
      { pattern: /^\/I4UProductInterest/i, transform: () => `/product-interest` },
      // Home/Dashboard
      { pattern: /^\/Dashboard/i, transform: () => `/home` },
      { pattern: /^\/Home/i, transform: () => `/home` },
    ];

    for (const mapping of urlMappings) {
      const match = backendUrl.match(mapping.pattern);
      if (match) {
        return mapping.transform(match);
      }
    }

    // If URL already starts with a valid I4U route, use it as-is
    const validI4UPrefixes = [
      '/home', '/application-', '/premium-', '/pay-premium', '/bonus-',
      '/agent-hub', '/product-interest', '/hospital-', '/profile',
      '/claim', '/policy-cancel', '/policy-change-request', '/agent-commission',
      '/pay-premium-from-bonus', '/policy-cancelation', '/policy-cancel-request',
      '/premium-history-list', '/I4UProduct',
    ];

    if (validI4UPrefixes.some(prefix => backendUrl.toLowerCase().startsWith(prefix.toLowerCase()))) {
      return backendUrl;
    }

    console.warn(`[Notification] No URL mapping found for: ${backendUrl}`);
    return null;
  }
}
